import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/api/client";
import type { JobProfileNoteListResponse } from "@/lib/api/model";

export type NotesScope =
  | { kind: "project"; projectId: string }
  | { kind: "job"; jobId: string };

export interface ProfileNoteAuthor {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

export interface ProfileNote {
  id: string;
  org_id: string;
  profile_id: string;
  project_id: string | null;
  job_id: string | null;
  parent_note_id: string | null;
  author_user_id: string | null;
  author: ProfileNoteAuthor | null;
  scope: "project" | "job";
  content: string;
  depth: number;
  like_count: number;
  dislike_count: number;
  viewer_reaction: "like" | "dislike" | null;
  created_at: string;
  updated_at: string;
  replies: ProfileNote[];
  /** "arbi" (authored here) | "ats" (imported from the connected ATS — read-only). */
  source?: "arbi" | "ats";
  /** Display fallback when the remote author has no Arbi user mapping. */
  ats_author_name?: string | null;
}

interface ProfileNoteListResponse {
  notes: ProfileNote[];
}

/** Project list is `{ notes }`; job list also carries `conversation_notes`. */
export type ProfileNotesListData =
  | ProfileNoteListResponse
  | JobProfileNoteListResponse;

function scopeKey(scope: NotesScope): string {
  return scope.kind === "project" ? scope.projectId : scope.jobId;
}

function listUrl(scope: NotesScope, profileId: string): string {
  return scope.kind === "project"
    ? `/api/projects/${scope.projectId}/profiles/${profileId}/notes`
    : `/api/jobs/${scope.jobId}/profiles/${profileId}/notes`;
}

export function profileNotesQueryKey(scope: NotesScope, profileId: string) {
  return ["profile-notes", scope.kind, scopeKey(scope), profileId] as const;
}

function updateNoteInTree(
  notes: ProfileNote[],
  noteId: string,
  updater: (note: ProfileNote) => ProfileNote | null,
): ProfileNote[] {
  return notes.reduce<ProfileNote[]>((acc, note) => {
    if (note.id === noteId) {
      const result = updater(note);
      if (result) acc.push(result);
      return acc;
    }
    acc.push({
      ...note,
      replies: updateNoteInTree(note.replies, noteId, updater),
    });
    return acc;
  }, []);
}

function addReplyToParent(
  notes: ProfileNote[],
  parentId: string,
  reply: ProfileNote,
): ProfileNote[] {
  return notes.map((note) => {
    if (note.id === parentId) {
      return { ...note, replies: [...note.replies, reply] };
    }
    return {
      ...note,
      replies: addReplyToParent(note.replies, parentId, reply),
    };
  });
}

// ── Query ───────────────────────────────────────────────────────

export function useProfileNotes(
  scope: NotesScope | null,
  profileId: string | null,
) {
  return useQuery({
    queryKey:
      scope && profileId
        ? profileNotesQueryKey(scope, profileId)
        : ["profile-notes", "disabled"],
    queryFn: () =>
      scope!.kind === "job"
        ? customInstance<JobProfileNoteListResponse>(
            listUrl(scope!, profileId!),
          )
        : customInstance<ProfileNoteListResponse>(listUrl(scope!, profileId!)),
    enabled: !!scope && !!profileId,
  });
}

// ── Create ──────────────────────────────────────────────────────

interface CreateNoteVars {
  content: string;
  parent_note_id?: string | null;
  parent_depth?: number;
  source_search_id?: string | null;
}

export function useCreateProfileNote(
  scope: NotesScope | null,
  profileId: string | null,
  currentUser?:
    | {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
        avatar_url?: string | null;
      }
    | null,
  sourceSearchId?: string | null,
) {
  const queryClient = useQueryClient();
  const qk = scope && profileId ? profileNotesQueryKey(scope, profileId) : null;

  return useMutation({
    mutationFn: (variables: CreateNoteVars) => {
      const { parent_depth: parentDepth, ...body } = variables;
      void parentDepth;
      const sourceSearch =
        scope?.kind === "project" ? sourceSearchId ?? null : null;
      return customInstance<ProfileNote>(listUrl(scope!, profileId!), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...body,
          source_search_id: body.source_search_id ?? sourceSearch,
        }),
      });
    },

    onMutate: async (variables) => {
      if (!qk || !scope) return;
      await queryClient.cancelQueries({ queryKey: qk });
      const previous = queryClient.getQueryData<ProfileNotesListData>(qk);

      const now = new Date().toISOString();
      const tempId = `temp-${crypto.randomUUID()}`;
      const optimistic: ProfileNote = {
        id: tempId,
        org_id: "",
        profile_id: profileId!,
        project_id: scope.kind === "project" ? scope.projectId : null,
        job_id: scope.kind === "job" ? scope.jobId : null,
        parent_note_id: variables.parent_note_id ?? null,
        author_user_id: currentUser?.id ?? "",
        author: currentUser
          ? {
              id: currentUser.id,
              email: currentUser.email,
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              avatar_url: currentUser.avatar_url ?? null,
            }
          : null,
        scope: scope.kind,
        content: variables.content,
        depth: variables.parent_note_id ? (variables.parent_depth ?? 0) + 1 : 0,
        like_count: 0,
        dislike_count: 0,
        viewer_reaction: null,
        created_at: now,
        updated_at: now,
        replies: [],
      };

      queryClient.setQueryData<ProfileNotesListData>(qk, (old) => {
        if (!old) return { notes: [optimistic] };
        const notes = (old.notes ?? []) as ProfileNote[];
        if (variables.parent_note_id) {
          return {
            ...old,
            notes: addReplyToParent(
              notes,
              variables.parent_note_id,
              optimistic,
            ),
          };
        }
        return { ...old, notes: [...notes, optimistic] };
      });

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (qk && context?.previous) {
        queryClient.setQueryData(qk, context.previous);
      }
    },

    onSettled: () => {
      if (qk) queryClient.invalidateQueries({ queryKey: qk });
    },
  });
}

// ── Update ──────────────────────────────────────────────────────

export function useUpdateProfileNote(
  scope: NotesScope | null,
  profileId: string | null,
  sourceSearchId?: string | null,
) {
  const queryClient = useQueryClient();
  const qk = scope && profileId ? profileNotesQueryKey(scope, profileId) : null;
  const sourceSearch = scope?.kind === "project" ? sourceSearchId ?? null : null;

  return useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) =>
      customInstance<ProfileNote>(`/api/profile-notes/${noteId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, source_search_id: sourceSearch }),
      }),

    onMutate: async ({ noteId, content }) => {
      if (!qk) return;
      await queryClient.cancelQueries({ queryKey: qk });
      const previous = queryClient.getQueryData<ProfileNotesListData>(qk);

      queryClient.setQueryData<ProfileNotesListData>(qk, (old) => {
        if (!old) return old;
        return {
          ...old,
          notes: updateNoteInTree(
            (old.notes ?? []) as ProfileNote[],
            noteId,
            (n) => ({
              ...n,
              content,
              updated_at: new Date().toISOString(),
            }),
          ),
        };
      });

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (qk && context?.previous) {
        queryClient.setQueryData(qk, context.previous);
      }
    },

    onSettled: () => {
      if (qk) queryClient.invalidateQueries({ queryKey: qk });
    },
  });
}

// ── Delete ──────────────────────────────────────────────────────

export function useDeleteProfileNote(
  scope: NotesScope | null,
  profileId: string | null,
) {
  const queryClient = useQueryClient();
  const qk = scope && profileId ? profileNotesQueryKey(scope, profileId) : null;

  return useMutation({
    mutationFn: (noteId: string) =>
      customInstance<void>(`/api/profile-notes/${noteId}`, { method: "DELETE" }),

    onMutate: async (noteId) => {
      if (!qk) return;
      await queryClient.cancelQueries({ queryKey: qk });
      const previous = queryClient.getQueryData<ProfileNotesListData>(qk);

      queryClient.setQueryData<ProfileNotesListData>(qk, (old) => {
        if (!old) return old;
        return {
          ...old,
          notes: updateNoteInTree(
            (old.notes ?? []) as ProfileNote[],
            noteId,
            () => null,
          ),
        };
      });

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (qk && context?.previous) {
        queryClient.setQueryData(qk, context.previous);
      }
    },

    onSettled: () => {
      if (qk) queryClient.invalidateQueries({ queryKey: qk });
    },
  });
}

// ── Set Reaction ────────────────────────────────────────────────

export function useSetNoteReaction(
  scope: NotesScope | null,
  profileId: string | null,
) {
  const queryClient = useQueryClient();
  const qk = scope && profileId ? profileNotesQueryKey(scope, profileId) : null;

  return useMutation({
    mutationFn: ({
      noteId,
      value,
    }: {
      noteId: string;
      value: "like" | "dislike";
    }) =>
      customInstance<ProfileNote>(`/api/profile-notes/${noteId}/reaction`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      }),

    onMutate: async ({ noteId, value }) => {
      if (!qk) return;
      await queryClient.cancelQueries({ queryKey: qk });
      const previous = queryClient.getQueryData<ProfileNotesListData>(qk);

      queryClient.setQueryData<ProfileNotesListData>(qk, (old) => {
        if (!old) return old;
        return {
          ...old,
          notes: updateNoteInTree(
            (old.notes ?? []) as ProfileNote[],
            noteId,
            (n) => {
              let { like_count, dislike_count } = n;
              if (n.viewer_reaction === "like") like_count--;
              if (n.viewer_reaction === "dislike") dislike_count--;
              if (value === "like") like_count++;
              if (value === "dislike") dislike_count++;
              return {
                ...n,
                viewer_reaction: value,
                like_count: Math.max(0, like_count),
                dislike_count: Math.max(0, dislike_count),
              };
            },
          ),
        };
      });

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (qk && context?.previous) {
        queryClient.setQueryData(qk, context.previous);
      }
    },

    onSettled: () => {
      if (qk) queryClient.invalidateQueries({ queryKey: qk });
    },
  });
}

// ── Delete Reaction ─────────────────────────────────────────────

export function useDeleteNoteReaction(
  scope: NotesScope | null,
  profileId: string | null,
) {
  const queryClient = useQueryClient();
  const qk = scope && profileId ? profileNotesQueryKey(scope, profileId) : null;

  return useMutation({
    mutationFn: (noteId: string) =>
      customInstance<ProfileNote>(`/api/profile-notes/${noteId}/reaction`, {
        method: "DELETE",
      }),

    onMutate: async (noteId) => {
      if (!qk) return;
      await queryClient.cancelQueries({ queryKey: qk });
      const previous = queryClient.getQueryData<ProfileNotesListData>(qk);

      queryClient.setQueryData<ProfileNotesListData>(qk, (old) => {
        if (!old) return old;
        return {
          ...old,
          notes: updateNoteInTree(
            (old.notes ?? []) as ProfileNote[],
            noteId,
            (n) => {
              let { like_count, dislike_count } = n;
              if (n.viewer_reaction === "like") like_count--;
              if (n.viewer_reaction === "dislike") dislike_count--;
              return {
                ...n,
                viewer_reaction: null,
                like_count: Math.max(0, like_count),
                dislike_count: Math.max(0, dislike_count),
              };
            },
          ),
        };
      });

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (qk && context?.previous) {
        queryClient.setQueryData(qk, context.previous);
      }
    },

    onSettled: () => {
      if (qk) queryClient.invalidateQueries({ queryKey: qk });
    },
  });
}
